import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Clock, Euro, CalendarCheck } from "lucide-react";
import { api } from "@/lib/api";
import { formatPrice, treatmentPath } from "@/lib/format";
import { useSite } from "@/context/AppContext";
import { Container, PageHero, Prose, Seo, SmartImage, SectionTitle } from "@/components/site/Ui";
import { CategoryCard, TreatmentCard } from "@/components/site/Cards";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function useFetch(url, deps = []) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  useEffect(() => {
    let alive = true;
    setState({ data: null, error: null, loading: true });
    api.get(url).then((r) => alive && setState({ data: r.data, error: null, loading: false }))
      .catch((e) => alive && setState({ data: null, error: e.response?.status || 500, loading: false }));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

export const Loading = () => <Container className="py-32 text-center text-[var(--muted-ink)]" data-testid="loading">Caricamento…</Container>;

export function NotFound() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    api.get("/public/resolve", { params: { path: pathname } }).then((r) => {
      if (r.data.redirect) nav(r.data.redirect, { replace: true });
      else setChecked(true);
    }).catch(() => setChecked(true));
  }, [pathname, nav]);
  if (!checked) return <Loading />;
  return (
    <>
      <Seo title="Pagina non trovata" noindex />
      <PageHero eyebrow="Errore 404" title="Questa pagina non esiste." intro="Il contenuto potrebbe essere stato spostato o rimosso.">
        <div className="mt-8 flex gap-4"><Link className="btn-primary" to="/" data-testid="notfound-home">Torna alla home</Link><Link className="btn-outline" to="/mappa-del-sito">Mappa del sito</Link></div>
      </PageHero>
    </>
  );
}

export function Treatments() {
  const { catalog } = useSite();
  if (!catalog) return <Loading />;
  const consult = catalog.treatments.filter((t) => t.kind === "consultation");
  return (
    <>
      <Seo title="Tutti i trattamenti" description="Elenco completo dei trattamenti di medicina estetica del centro, divisi per categoria." path="/trattamenti" />
      <PageHero eyebrow="Trattamenti" title="Tutti i trattamenti" intro="Ogni trattamento è preceduto da una valutazione medica. Esplora le categorie o consulta il listino." crumbs={[{ label: "Trattamenti" }]} />
      <Container className="py-20">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{catalog.categories.map((c, i) => <CategoryCard key={c.id} c={c} index={i} />)}</div>
        {catalog.categories.map((c) => (
          <section key={c.id} className="mt-20" aria-labelledby={`cat-${c.slug}`}>
            <div className="flex items-end justify-between gap-4 border-b border-[var(--line)] pb-4">
              <h2 id={`cat-${c.slug}`} className="text-3xl sm:text-4xl">{c.name}</h2>
              <Link to={`/trattamenti/${c.slug}`} className="text-sm text-[var(--petrol)] hover:underline">Vai alla categoria</Link>
            </div>
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">{catalog.treatments.filter((t) => t.category_id === c.id).map((t) => <TreatmentCard key={t.id} t={t} />)}</div>
          </section>
        ))}
        {consult.length > 0 && (
          <section className="mt-20"><h2 className="border-b border-[var(--line)] pb-4 text-3xl sm:text-4xl">Consulenza</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">{consult.map((t) => <TreatmentCard key={t.id} t={t} />)}</div></section>
        )}
      </Container>
    </>
  );
}

export function Category() {
  const { cat } = useParams();
  const { data, error, loading } = useFetch(`/public/categories/${cat}`, [cat]);
  if (loading) return <Loading />;
  if (error) return <NotFound />;
  const c = data.category;
  return (
    <>
      <Seo title={c.seo_title || c.name} description={c.seo_description || c.intro} path={`/trattamenti/${c.slug}`} />
      <PageHero eyebrow="Categoria" title={c.name} intro={c.intro} crumbs={[{ label: "Trattamenti", to: "/trattamenti" }, { label: c.name }]} />
      <Container className="grid gap-14 py-20 lg:grid-cols-3">
        <Prose html={c.body_html} className="lg:col-span-1" />
        <div className="grid gap-6 md:grid-cols-2 lg:col-span-2">{data.treatments.map((t) => <TreatmentCard key={t.id} t={{ ...t, category_name: c.name }} />)}</div>
      </Container>
    </>
  );
}

function Facts({ t }) {
  const items = [
    [Euro, "Prezzo", formatPrice(t.price_type, t.price_cents), "treatment-price"],
    [Clock, "Durata appuntamento", `${t.duration_min} minuti`, "treatment-duration"],
  ];
  return (
    <aside className="card-soft h-fit p-7 lg:sticky lg:top-28" data-testid="treatment-facts">
      {items.map(([Icon, label, value, id]) => (
        <div key={label} className="flex items-start gap-3 border-b border-[var(--line)] py-4 first:pt-0">
          <Icon size={18} className="mt-1 text-[var(--brass-text)]" />
          <div><p className="text-xs uppercase tracking-widest text-[var(--muted-ink)]">{label}</p><p className="mt-1 text-lg font-medium" data-testid={id}>{value}</p></div>
        </div>
      ))}
      {t.price_note && <p className="pt-3 text-xs text-[var(--muted-ink)]">{t.price_note}</p>}
      {t.bookable ? (
        <Link to={`/prenota?t=${t.id}`} className="btn-primary mt-6 w-full justify-center" data-testid="treatment-book-btn"><CalendarCheck size={18} /> Prenota</Link>
      ) : (
        <Link to="/contatti" className="btn-outline mt-6 w-full justify-center">Richiedi informazioni</Link>
      )}
    </aside>
  );
}

export function Treatment() {
  const { slug, cat } = useParams();
  const { data, error, loading } = useFetch(`/public/treatments/${slug}`, [slug]);
  const nav = useNavigate();
  useEffect(() => {
    if (data && data.treatment.category_slug !== cat) nav(`/trattamenti/${data.treatment.category_slug}/${slug}`, { replace: true });
  }, [data, cat, slug, nav]);
  if (loading) return <Loading />;
  if (error) return <NotFound />;
  const { treatment: t, category: c, related, gallery_count } = data;
  const faqLd = t.faqs?.length ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: t.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) } : null;
  return (
    <>
      <Seo title={t.seo_title || t.name} description={t.seo_description || t.short_description} path={treatmentPath(t)} jsonLd={faqLd} />
      <PageHero eyebrow={c?.name || "Consulenza"} title={t.name} intro={t.short_description} crumbs={[{ label: "Trattamenti", to: "/trattamenti" }, ...(c ? [{ label: c.name, to: `/trattamenti/${c.slug}` }] : []), { label: t.name }]} />
      <Container className="grid gap-14 py-20 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {t.image_media_id && <SmartImage mediaId={t.image_media_id} alt={t.image_alt} width={1600} height={1000} className="mb-10 aspect-[16/10] w-full rounded-2xl object-cover" />}
          <Prose html={t.body_html} />
          {t.faqs?.length > 0 && (
            <section className="mt-14"><h2 className="text-3xl">Domande frequenti</h2>
              <Accordion type="single" collapsible className="mt-6">
                {t.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left text-base" data-testid={`treatment-faq-${i}`}>{f.q}</AccordionTrigger><AccordionContent className="text-[var(--muted-ink)]">{f.a}</AccordionContent></AccordionItem>
                ))}
              </Accordion>
            </section>
          )}
          <div className="mt-14 flex flex-wrap gap-4">
            <Link to={gallery_count > 0 ? `/galleria?trattamento=${t.slug}` : "/galleria"} className="btn-outline" data-testid="treatment-gallery-link">{gallery_count > 0 ? `Prima e dopo (${gallery_count})` : "Prima e dopo"}</Link>
            <Link to="/listino-prezzi" className="btn-outline">Listino prezzi</Link>
          </div>
        </div>
        <Facts t={t} />
      </Container>
      {related.length > 0 && (
        <section className="bg-[var(--ivory-soft)] py-16"><Container><SectionTitle title="Nella stessa categoria" />
          <ul className="mt-8 flex flex-wrap gap-3">{related.map((r) => <li key={r.slug}><Link className="rounded-full border border-[var(--line)] bg-white px-5 py-2.5 text-sm hover:border-[var(--brass)]" to={`/trattamenti/${r.category_slug}/${r.slug}`}>{r.name}</Link></li>)}</ul>
        </Container></section>
      )}
    </>
  );
}

export function Prices() {
  const { catalog } = useSite();
  if (!catalog) return <Loading />;
  const groups = [...catalog.categories.map((c) => ({ name: c.name, slug: c.slug, items: catalog.treatments.filter((t) => t.category_id === c.id) })),
    { name: "Consulenza", slug: "consulenza", items: catalog.treatments.filter((t) => t.kind === "consultation") }].filter((g) => g.items.length);
  return (
    <>
      <Seo title="Listino prezzi" description="Prezzi e durata dei trattamenti di medicina estetica." path="/listino-prezzi" />
      <PageHero eyebrow="Trasparenza" title="Listino prezzi" intro="I prezzi 'da' sono indicativi: il preventivo definitivo viene concordato dopo la visita medica." crumbs={[{ label: "Listino prezzi" }]} />
      <Container className="max-w-4xl py-20">
        {groups.map((g) => (
          <section key={g.slug} className="mb-16" aria-labelledby={`p-${g.slug}`}>
            <h2 id={`p-${g.slug}`} className="text-3xl">{g.name}</h2>
            <span className="brass-rule mt-4" />
            <table className="mt-6 w-full text-left" data-testid={`price-table-${g.slug}`}>
              <thead className="sr-only"><tr><th>Trattamento</th><th>Durata</th><th>Prezzo</th></tr></thead>
              <tbody>{g.items.map((t) => (
                <tr key={t.id} className="border-b border-[var(--line)]">
                  <td className="py-5"><Link to={treatmentPath(t)} className="font-medium hover:text-[var(--petrol)]">{t.name}</Link>{t.price_note && <span className="block text-xs text-[var(--muted-ink)]">{t.price_note}</span>}</td>
                  <td className="py-5 text-sm text-[var(--muted-ink)]">{t.duration_min} min</td>
                  <td className="py-5 text-right font-serif text-xl text-[var(--petrol)]" data-testid={`price-${t.slug}`}>{formatPrice(t.price_type, t.price_cents)}</td>
                </tr>))}</tbody>
            </table>
          </section>
        ))}
      </Container>
    </>
  );
}
